import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { GridFilter } from '../../models/grid.model';

export interface ToolbarFilter {
  /** Field name in the API model */
  field: string;
  /** Operator: 'contains', 'eq', 'gte', 'lte' */
  op?: string;
  /** Display label (shown above date inputs and as select placeholder) */
  label?: string;
  /** If provided renders a select instead of a text/date input */
  options?: { value: string; label: string }[];
  /** Placeholder text for text search inputs */
  placeholder?: string;
  /** Input type: 'text' (default) | 'date' | 'select' (auto-detected via options) */
  type?: 'text' | 'date' | 'select';
  /**
   * Internal state key — override when the same field appears twice
   * with different operators (e.g. two date filters on 'createdAt').
   * Defaults to `field` when omitted.
   */
  key?: string;
}

@Component({
  selector: 'app-grid-toolbar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './grid-toolbar.component.html',
  styleUrls: ['./grid-toolbar.component.scss'],
})
export class GridToolbarComponent implements OnInit, OnDestroy {
  /** Fields to expose in the toolbar */
  @Input() toolbarFilters: ToolbarFilter[] = [];
  /** Whether to show the export button */
  @Input() showExport = true;
  /** Debounce for the text search (ms) */
  @Input() debounceMs = 350;

  /** Emits serialised filter string whenever any filter changes */
  @Output() filtersChange = new EventEmitter<string>();
  /** Emits the chosen export format when user selects an export option */
  @Output() exportClick = new EventEmitter<'csv' | 'xlsx'>();

  /** Internal state: keyed by field */
  values: Record<string, string> = {};
  exportOpen = false;

  private readonly textSearch$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Pre-populate select and date fields so default option / empty value matches from the start
    this.toolbarFilters.forEach(f => {
      const k = f.key ?? f.field;
      if ((f.options || f.type === 'date') && this.values[k] == null) {
        this.values[k] = '';
      }
    });

    this.textSearch$
      .pipe(debounceTime(this.debounceMs), takeUntil(this.destroy$))
      .subscribe(() => this.emit());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTextChange(): void { this.textSearch$.next(); }
  onSelectChange(): void { this.emit(); }
  onDateChange(): void { this.emit(); }

  clearAll(): void {
    this.values = {};
    this.emit();
  }

  toggleExportMenu(): void {
    this.exportOpen = !this.exportOpen;
  }

  onExport(type: 'csv' | 'xlsx'): void {
    this.exportOpen = false;
    this.exportClick.emit(type);
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.values).some(v => v != null && v !== '');
  }

  private emit(): void {
    const filters: GridFilter[] = this.toolbarFilters
      .filter(f => {
        const v = this.values[f.key ?? f.field];
        return v != null && v !== '';
      })
      .map(f => ({ field: f.field, op: f.op ?? 'contains', value: this.values[f.key ?? f.field] }));

    this.filtersChange.emit(filters.length ? JSON.stringify(filters) : '');
  }
}
